import React, { useState, useMemo, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2, Search, Mail, Users, Save, X, CheckCircle, UserPlus, Edit2, MapPin, Phone, Receipt, Download, Send, Unlink, Award, BadgeCheck, Sparkles, AlertTriangle,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import ConsumerAccountInvoicePanel from './ConsumerAccountInvoicePanel';
import ConsumerAccountEntriesPanel from './ConsumerAccountEntriesPanel';
import UnallocatedOrdersView from './UnallocatedOrdersView';

// Consumer Accounts tab — lightweight customer records imported from Sage that
// don't have a registered User account yet. Admins can add an email address and
// send a branded "create your account" invitation so the consumer can sign up.

export default function ConsumerAccountsTab({ consumerAccounts, setConsumerAccounts, orders }) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [retailOrders, setRetailOrders] = useState([]);
  const [expandedInvoicesId, setExpandedInvoicesId] = useState(null);
  const [view, setView] = useState('accounts'); // 'accounts' | 'unallocated'
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [bulkInviting, setBulkInviting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkSage, setBulkSage] = useState(false);
  const [bulkSeeding, setBulkSeeding] = useState(false);

  const STALE_MS = 14 * 86400000;
  const accountIds = useMemo(() => (consumerAccounts || []).map((a) => a.id), [consumerAccounts]);
  const { data: seedData } = useQuery({
    queryKey: ['consumer-seed-status', accountIds.length],
    queryFn: () => base44.functions.invoke('consumerAccountsBulkAction', { mode: 'seed_status', account_ids: accountIds }).then((r) => r.data?.results || []),
    enabled: view === 'accounts' && accountIds.length > 0,
  });
  const seedMap = useMemo(() => {
    const m = {};
    for (const r of (seedData || [])) m[r.id] = r;
    return m;
  }, [seedData]);

  // Fetch consumer (retail) Orders so we can show + allocate their invoices.
  const [compEntries, setCompEntries] = useState([]);
  const reloadOrders = async () => {
    try {
      const [list, entries] = await Promise.all([
        base44.entities.Order.list('-created_date', 500),
        base44.entities.CompetitionEntry.filter({ is_paid: true }),
      ]);
      setRetailOrders(list || []);
      setCompEntries(entries || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { reloadOrders(); }, []);

  // After the unallocated view creates accounts, refresh both the orders and
  // the ConsumerAccount list (parent state) so the new accounts appear here.
  const handleProcessed = async () => {
    await reloadOrders();
    try {
      const consumers = await base44.entities.ConsumerAccount.list('-created_date', 500);
      setConsumerAccounts(consumers || []);
    } catch (e) { /* ignore */ }
  };

  const bulkInviteSelected = async () => {
    const ids = [...selectedAccounts];
    if (!ids.length) return;
    setBulkInviting(true);
    try {
      const res = await base44.functions.invoke('consumerAccountsBulkAction', { mode: 'invite_existing', send_invite: true, account_ids: ids });
      const ok = (res.data?.results || []).filter((r) => r.ok).length;
      const fail = (res.data?.results || []).filter((r) => !r.ok);
      if (ok) toast({ title: `${ok} invite${ok !== 1 ? 's' : ''} sent` });
      if (fail.length) toast({ title: `${fail.length} failed`, description: fail.map((f) => f.error).join('; ').slice(0, 120), variant: 'destructive' });
      const now = new Date().toISOString();
      setConsumerAccounts((prev) => prev.map((a) => ids.includes(a.id) ? { ...a, invited_at: now } : a));
      setSelectedAccounts(new Set());
    } catch (e) {
      toast({ title: 'Invite failed', description: e.message, variant: 'destructive' });
    }
    setBulkInviting(false);
  };

  const bulkSageExport = async () => {
    const ids = [...selectedAccounts];
    if (!ids.length) return;
    setBulkSage(true);
    try {
      const res = await base44.functions.invoke('exportCustomerToSage', { account_type: 'consumer', account_ids: ids });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Export failed');
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: `Exported ${res.data.count || ids.length} accounts to Sage CSV` });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
    setBulkSage(false);
  };

  const bulkSeedLoyalty = async () => {
    const ids = [...selectedAccounts];
    if (!ids.length) return;
    setBulkSeeding(true);
    try {
      const res = await base44.functions.invoke('consumerAccountsBulkAction', { mode: 'seed_loyalty', account_ids: ids });
      const results = res.data?.results || [];
      const seeded = results.filter((r) => r.seeded).length;
      const skipped = results.filter((r) => r.ok && !r.seeded).length;
      const failed = results.filter((r) => !r.ok).length;
      toast({
        title: seeded ? `${seeded} account${seeded !== 1 ? 's' : ''} seeded with loyalty points` : 'No new points awarded',
        description: [skipped && `${skipped} already seeded / no spend`, failed && `${failed} failed`].filter(Boolean).join(' · ') || undefined,
      });
    } catch (e) {
      toast({ title: 'Seed failed', description: e.message, variant: 'destructive' });
    }
    setBulkSeeding(false);
  };

  const toggleAccount = (id) => setSelectedAccounts((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const allocateOrder = (orderId, accountId) => {
    setRetailOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, consumer_account_id: accountId } : o)));
  };
  const unallocateOrder = (orderId) => {
    setRetailOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, consumer_account_id: null } : o)));
  };

  // Count linked retail invoices per consumer account
  const orderCounts = useMemo(() => {
    const map = {};
    for (const o of retailOrders) {
      if (o.consumer_account_id) map[o.consumer_account_id] = (map[o.consumer_account_id] || 0) + 1;
    }
    return map;
  }, [retailOrders]);

  // Count paid competition entries linked to each consumer account.
  const entryCounts = useMemo(() => {
    const map = {};
    for (const e of compEntries) {
      if (e.consumer_account_id) map[e.consumer_account_id] = (map[e.consumer_account_id] || 0) + 1;
    }
    return map;
  }, [compEntries]);

  // Accounts already linked to a registered user are consolidated and hidden
  // from the "needs attention" list (they keep their orders via consumer_account_id).
  const allAccounts = consumerAccounts || [];
  const registeredCount = allAccounts.filter((a) => a.linked_user_id).length;
  const withEmail = allAccounts.filter((a) => a.email).length;
  const invited = allAccounts.filter((a) => a.invited_at).length;
  const conversion = invited ? Math.round((registeredCount / invited) * 100) : 0;
  const staleCount = allAccounts.filter((a) => a.invited_at && !a.linked_user_id && (Date.now() - new Date(a.invited_at).getTime()) > STALE_MS).length;

  const filtered = allAccounts.filter((a) => {
    if (statusFilter === 'uninvited' && a.invited_at) return false;
    if (statusFilter === 'pending' && (!a.invited_at || a.linked_user_id)) return false;
    if (statusFilter === 'registered' && !a.linked_user_id) return false;
    if (statusFilter === 'stale' && !(a.invited_at && !a.linked_user_id && (Date.now() - new Date(a.invited_at).getTime()) > STALE_MS)) return false;
    if (statusFilter === 'unseeded') {
      const s = seedMap[a.id];
      if (!(s && s.has_paid_orders && !s.seeded_points)) return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.customer_name?.toLowerCase().includes(q) ||
      a.sage_account_ref?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.address_postcode?.toLowerCase().includes(q)
    );
  });

  const [exporting, setExporting] = useState(null);
  const exportToSage = async (account) => {
    setExporting(account.id);
    try {
      const res = await base44.functions.invoke('exportCustomerToSage', { account_type: 'consumer', account_id: account.id });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Export failed');
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: 'Sage customer CSV exported', description: account.customer_name });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
    setExporting(null);
  };

  const saveEmail = async (account) => {
    const email = emailDraft.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast({ title: 'Enter a valid email address', variant: 'destructive' });
      return;
    }
    setSavingEmail(account.id);
    try {
      await base44.entities.ConsumerAccount.update(account.id, { email });
      setConsumerAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, email } : a)));
      setEditingId(null);
      setEmailDraft('');
      toast({ title: 'Email saved', description: email });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    }
    setSavingEmail(null);
  };

  const sendInvite = async (account) => {
    if (!account.email) {
      toast({ title: 'Add an email address first', variant: 'destructive' });
      return;
    }
    setInviting(account.id);
    try {
      const res = await base44.functions.invoke('b2bInviteConsumer', {
        consumer_account_id: account.id,
        email: account.email,
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Invite failed');
      const now = new Date().toISOString();
      setConsumerAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, invited_at: now, email: res.data.email } : a)));
      toast({ title: 'Sign-up email sent', description: `Invitation sent to ${res.data.email}` });
    } catch (e) {
      toast({ title: 'Invite failed', description: e.message, variant: 'destructive' });
    }
    setInviting(null);
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="border border-white/10 bg-white/[0.02] p-4">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-white/70 font-bold">Consumer accounts</span> are customer records imported from Sage that don't have a registered online account yet.
          Add their email address and send a sign-up invitation so they can create their account and see their order history.
        </p>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 border border-white/10 bg-[#111115] p-1 w-full sm:w-fit">
        <button onClick={() => setView('accounts')} className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold tracking-wider transition-colors ${view === 'accounts' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}>
          <Users className="w-3.5 h-3.5" /> CONSUMER ACCOUNTS
        </button>
        <button onClick={() => setView('unallocated')} className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold tracking-wider transition-colors ${view === 'unallocated' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}>
          <Unlink className="w-3.5 h-3.5" /> UNALLOCATED ORDERS
        </button>
      </div>

      {view === 'unallocated' ? (
        <UnallocatedOrdersView orders={retailOrders} consumerAccounts={consumerAccounts} onProcessed={handleProcessed} />
      ) : (
      <React.Fragment>
      {/* Invite funnel */}
      <div className="border border-white/10 bg-[#111115] p-4 flex items-center gap-6 flex-wrap">
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">INVITE FUNNEL</p>
          <p className="text-white text-sm">{invited} invited → {registeredCount} registered</p>
        </div>
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">CONVERSION</p>
          <p className="text-primary font-black text-lg">{conversion}%</p>
        </div>
        {staleCount > 0 && (
          <button onClick={() => setStatusFilter('stale')} className="ml-auto flex items-center gap-1.5 border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400/20 text-amber-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors">
            <AlertTriangle className="w-3.5 h-3.5" /> {staleCount} STALE INVITES
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-white/10 bg-[#111115] p-4 text-center">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TOTAL</p>
          <p className="text-white font-black text-2xl">{allAccounts.length}</p>
        </div>
        <div className="border border-cyan-400/20 bg-cyan-400/[0.03] p-4 text-center">
          <p className="text-cyan-400/70 text-[10px] font-bold tracking-widest mb-1">WITH EMAIL</p>
          <p className="text-cyan-400 font-black text-2xl">{withEmail}</p>
        </div>
        <div className="border border-green-400/20 bg-green-400/[0.03] p-4 text-center">
          <p className="text-green-400/70 text-[10px] font-bold tracking-widest mb-1">INVITED</p>
          <p className="text-green-400 font-black text-2xl">{invited}</p>
        </div>
        <div className="border border-primary/20 bg-primary/[0.03] p-4 text-center">
          <p className="text-primary/70 text-[10px] font-bold tracking-widest mb-1">REGISTERED</p>
          <p className="text-primary font-black text-2xl">{registeredCount}</p>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-1 flex-wrap">
        {[['all', 'ALL'], ['uninvited', 'UNINVITED'], ['pending', 'PENDING'], ['registered', 'REGISTERED'], ['stale', 'STALE'], ['unseeded', 'UNSEEDED']].map(([val, label]) => (
          <button key={val} onClick={() => setStatusFilter(val)} className={`px-3 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${statusFilter === val ? 'bg-primary text-white' : 'border border-white/10 text-white/50 hover:text-white'}`}>{label}</button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, Sage ref, email, postcode…"
          className="seo-input pl-9"
        />
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">NO CONSUMER ACCOUNTS</p>
          <p className="text-xs mt-1">Import Sage customer data to create consumer records</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((account) => (
            <div key={account.id} className={`border bg-[#111115] p-4 ${selectedAccounts.has(account.id) ? 'border-primary/40' : 'border-white/10'}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <input
                  type="checkbox"
                  checked={selectedAccounts.has(account.id)}
                  onChange={() => toggleAccount(account.id)}
                  className="w-4 h-4 accent-primary mt-1.5 cursor-pointer shrink-0"
                />
                <div className="w-9 h-9 bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-sm">
                  {account.customer_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-bold text-sm">{account.customer_name}</p>
                    <span className="text-white/40 text-[10px] font-mono border border-white/15 px-1.5 py-0.5">{account.sage_account_ref}</span>
                    {orderCounts[account.id] > 0 && (
                      <span className="text-cyan-400/70 text-[10px] font-bold tracking-widest">{orderCounts[account.id]} ORDER{orderCounts[account.id] !== 1 ? 'S' : ''}</span>
                    )}
                    {entryCounts[account.id] > 0 && (
                      <span className="text-orange-400/80 text-[10px] font-bold tracking-widest">{entryCounts[account.id]} COMP ENTR{entryCounts[account.id] !== 1 ? 'IES' : 'Y'}</span>
                    )}
                    {account.inactive && <span className="text-red-400 text-[10px] font-bold tracking-widest">INACTIVE</span>}
                    {account.linked_user_id && <span className="text-primary text-[10px] font-bold tracking-widest flex items-center gap-1"><BadgeCheck className="w-3 h-3" />REGISTERED</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1 text-xs text-white/40">
                    {(account.address_line1 || account.address_city) && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[account.address_line1, account.address_city, account.address_postcode].filter(Boolean).join(', ')}</span>
                    )}
                    {account.phone && <a href={`tel:${account.phone}`} className="flex items-center gap-1 hover:text-white"><Phone className="w-3 h-3" />{account.phone}</a>}
                  </div>
                  {account.invited_at && (
                    <p className="text-green-400/80 text-[10px] mt-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Invited {new Date(account.invited_at).toLocaleDateString('en-GB')}</p>
                  )}
                  {(() => {
                    const s = seedMap[account.id];
                    if (!s) return null;
                    if (s.seeded_points > 0) return <p className="text-amber-400/80 text-[10px] mt-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> {s.seeded_points} loyalty pts seeded</p>;
                    if (s.has_paid_orders) return <p className="text-white/30 text-[10px] mt-1 flex items-center gap-1">Unseeded · has paid orders</p>;
                    return null;
                  })()}
                </div>

                {/* Email + invite actions */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <button
                    onClick={() => setExpandedInvoicesId(expandedInvoicesId === account.id ? null : account.id)}
                    className={`inline-flex items-center gap-1.5 border font-bold text-[10px] tracking-widest px-3 py-2 transition-colors ${expandedInvoicesId === account.id ? 'border-primary bg-primary text-white' : 'border-white/15 hover:border-white/30 text-white/50 hover:text-white'}`}
                    title="View and allocate invoices for this account"
                  >
                    <Receipt className="w-3.5 h-3.5" /> INVOICES{orderCounts[account.id] > 0 ? ` (${orderCounts[account.id]})` : ''}
                  </button>
                  <button
                    onClick={() => exportToSage(account)}
                    disabled={exporting === account.id}
                    title="Export this customer record to a Sage 50 Customer Record import CSV"
                    className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    {exporting === account.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    SAGE
                  </button>
                  {editingId === account.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        placeholder="customer@email.com"
                        className="seo-input w-56"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(account); if (e.key === 'Escape') { setEditingId(null); setEmailDraft(''); } }}
                      />
                      <button
                        onClick={() => saveEmail(account)}
                        disabled={savingEmail === account.id}
                        className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-[10px] tracking-widest px-3 py-2 transition-colors"
                      >
                        {savingEmail === account.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} SAVE
                      </button>
                      <button onClick={() => { setEditingId(null); setEmailDraft(''); }} className="text-white/30 hover:text-white p-1.5"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <>
                      {account.email ? (
                        <span className="text-cyan-400/80 text-xs flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{account.email}</span>
                      ) : (
                        <span className="text-white/30 text-xs">No email on file</span>
                      )}
                      <button
                        onClick={() => { setEditingId(account.id); setEmailDraft(account.email || ''); }}
                        className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-2 transition-colors"
                        title={account.email ? 'Edit email address' : 'Add email address'}
                      >
                        <Edit2 className="w-3.5 h-3.5" /> {account.email ? 'EDIT' : 'ADD EMAIL'}
                      </button>
                      <button
                        onClick={() => sendInvite(account)}
                        disabled={inviting === account.id || !account.email}
                        className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={account.email ? 'Send sign-up invitation' : 'Add an email first'}
                      >
                        {inviting === account.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                        {account.invited_at ? 'RESEND' : 'SEND SIGN-UP'}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {expandedInvoicesId === account.id && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <ConsumerAccountInvoicePanel
                    accountId={account.id}
                    accountName={account.customer_name}
                    orders={retailOrders}
                    onAllocated={allocateOrder}
                    onUnallocated={unallocateOrder}
                  />
                  <div className="mt-4">
                    <ConsumerAccountEntriesPanel accountId={account.id} entries={compEntries} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </React.Fragment>
      )}

      {/* Bulk actions bar */}
      {view === 'accounts' && selectedAccounts.size > 0 && (
        <div className="sticky bottom-0 z-20 bg-[#111115] border-t border-primary/30 px-4 py-3 flex items-center gap-2 flex-wrap">
          <span className="bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-full">{selectedAccounts.size} selected</span>
          <button onClick={() => setSelectedAccounts(new Set())} className="text-white/40 hover:text-white text-xs font-bold tracking-wider flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> CLEAR</button>
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            <button onClick={bulkSageExport} disabled={bulkSage} title="Export selected accounts to a Sage customer import CSV" className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-xs tracking-wider px-3 py-2 disabled:opacity-50">
              {bulkSage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} SAGE
            </button>
            <button onClick={bulkSeedLoyalty} disabled={bulkSeeding} title="Backfill loyalty points from each account's historical order spend" className="inline-flex items-center gap-1.5 border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400 hover:text-black text-amber-400 font-bold text-xs tracking-wider px-3 py-2 disabled:opacity-50">
              {bulkSeeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />} SEED LOYALTY
            </button>
            <button onClick={bulkInviteSelected} disabled={bulkInviting} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2 disabled:opacity-50">
              {bulkInviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} SEND SIGN-UP
            </button>
          </div>
        </div>
      )}
    </div>
  );
}